import React from 'react';
import { DollarSignIcon, TrendingUpIcon, MinusIcon } from 'lucide-react';

interface Project {
  id?: number;
  project_name?: string;
  transaction_count?: number;
  total_revenue?: number;
  average_revenue?: number;
  total_cost?: number;
  average_roi?: number;
}

interface ROICalculatorProps {
  data?: Project[];
}

export default function ROICalculator({ data }: ROICalculatorProps) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 mb-2">No revenue data available yet</p>
          <p className="text-sm text-gray-400">
            Track conversions with revenue to see ROI metrics
          </p>
        </div>
      </div>
    );
  }

  // Calculate totals
  const totals = data.reduce(
    (acc, project) => ({
      revenue: acc.revenue + (project.total_revenue || 0),
      cost: acc.cost + (project.total_cost || 0),
      transactions: acc.transactions + (project.transaction_count || 0),
    }),
    { revenue: 0, cost: 0, transactions: 0 }
  );

  const totalProfit = totals.revenue - totals.cost;
  const totalROI = totals.cost > 0 ? ((totalProfit / totals.cost) * 100) : 0;

  // Sort by ROI
  const sortedProjects = [...data].sort(
    (a, b) => (b.average_roi || 0) - (a.average_roi || 0)
  );

  return (
    <div className="space-y-6">
      {/* Total Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <DollarSignIcon className="w-5 h-5" />
            <p className="text-sm font-medium opacity-90">Total Revenue</p>
          </div>
          <p className="text-3xl font-bold">
            ${totals.revenue.toLocaleString()}
          </p>
        </div>

        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <MinusIcon className="w-5 h-5" />
            <p className="text-sm font-medium opacity-90">Total Cost</p>
          </div>
          <p className="text-3xl font-bold">
            ${totals.cost.toLocaleString()}
          </p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <DollarSignIcon className="w-5 h-5" />
            <p className="text-sm font-medium opacity-90">Net Profit</p>
          </div>
          <p className="text-3xl font-bold">
            ${totalProfit.toLocaleString()}
          </p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-4 text-white">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUpIcon className="w-5 h-5" />
            <p className="text-sm font-medium opacity-90">Average ROI</p>
          </div>
          <p className="text-3xl font-bold">
            {totalROI.toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Project Breakdown Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Transactions
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Revenue
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cost
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Profit
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                ROI
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProjects.map((project) => {
              const profit = (project.total_revenue || 0) - (project.total_cost || 0);
              const roi = project.average_roi || 0;
              const roiColor = roi >= 100 ? 'text-green-600' : roi >= 50 ? 'text-yellow-600' : 'text-red-600';

              return (
                <tr key={project.id || project.project_name} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {project.project_name || 'Unknown Project'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                    {project.transaction_count || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    ${(project.total_revenue || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                    ${(project.total_cost || 0).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    ${profit.toLocaleString()}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-right text-sm font-bold ${roiColor}`}>
                    {roi.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ROI Guide */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Understanding ROI</h4>
        <p className="text-sm text-blue-800">
          ROI (Return on Investment) = ((Revenue - Cost) / Cost) × 100%
        </p>
        <div className="mt-3 flex gap-4 text-xs text-blue-700">
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            Excellent (≥100%)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
            Good (≥50%)
          </span>
          <span className="flex items-center gap-1">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            Needs Improvement (&lt;50%)
          </span>
        </div>
      </div>
    </div>
  );
}
